/**
 * Tile-layer rendering — paints the visible tile range each frame as
 * procedural isometric diamonds. One PixiJS `Graphics` object backs the
 * entire tile layer; on each render pass we `.clear()` and re-draw only
 * the in-viewport tiles (viewport culling per §4.8).
 *
 * This is the floor-level primitive. Walls / props / avatars sit above.
 * No pack atlas in Phase 1 (Q-C8) — the diamonds ship as solid colours
 * sampled from `MATERIAL_COLORS`.
 */

import { Graphics } from 'pixi.js';

import type { WorldModel } from '../world/model';
import {
  tileToIso,
  TILE_HEIGHT_PX,
  TILE_WIDTH_PX,
  visibleTileRange,
  type CameraState,
} from './iso';
import { biomeTint, materialColor } from './palette';
import type { SceneLayers } from './scene';

export interface TileRenderer {
  /** Re-paint the visible tile range. Call from the ticker each frame
   *  (or on-change when a dirty-flag scheme is added later). */
  render: (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
  ) => void;
  destroy: () => void;
}

/**
 * Attach a tile renderer to `layers.tileLayer`. The renderer owns exactly
 * one Graphics object; higher draw counts are a D17 performance concern,
 * not a D6 correctness one.
 */
export function createTileRenderer(layers: SceneLayers): TileRenderer {
  const g = new Graphics();
  layers.tileLayer.addChild(g);

  const halfW = TILE_WIDTH_PX / 2;
  const halfH = TILE_HEIGHT_PX / 2;

  const render = (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
  ): void => {
    g.clear();
    if (model.width === 0 || model.height === 0) return;

    const r = visibleTileRange(cam, viewportW, viewportH, 2);
    const minX = Math.max(r.minX, 0);
    const minY = Math.max(r.minY, 0);
    const maxX = Math.min(r.maxX, model.width - 1);
    const maxY = Math.min(r.maxY, model.height - 1);

    for (let y = minY; y <= maxY; y++) {
      const rowBase = y * model.width;
      for (let x = minX; x <= maxX; x++) {
        const idx = rowBase + x;
        const material = model.tileMaterials[idx] ?? 0;
        const biome = model.tileBiomes[idx] ?? 0;
        const baseColor = materialColor(material);
        const tint = biomeTint(biome);
        const elev = model.tileElevations[idx] ?? 0;
        const blended = blendColors(blendColors(baseColor, tint), elevationTint(elev));

        const { x: px, y: py } = tileToIso(x, y);
        // Diamond centred at (px, py):
        //   top → right → bottom → left
        g.poly([
          px,
          py - halfH,
          px + halfW,
          py,
          px,
          py + halfH,
          px - halfW,
          py,
        ]);
        g.fill({ color: blended, alpha: 1 });
      }
    }
  };

  return {
    render,
    destroy: () => {
      g.destroy();
    },
  };
}

/** Elevation zone tint — higher tiles darken subtly. Thresholds match
 *  ELEVATION.md §2.3 mapped to u8 space (M2.2 Q-1). */
export function elevationTint(elevation: number): number {
  if (elevation <= 61) return 0xFFFFFF;
  if (elevation <= 140) return 0xF8F8F0;
  if (elevation <= 199) return 0xE8E8E0;
  if (elevation <= 240) return 0xE0E0E0;
  return 0xD8D8D8;
}

/** Multiply two 0xRRGGBB colours — used to fold a subtle biome tint over
 *  the base material colour. Result stays in 0xRRGGBB space. */
export function blendColors(a: number, b: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = (ar * br) / 255;
  const gn = (ag * bg) / 255;
  const bl = (ab * bb) / 255;
  return ((r & 0xff) << 16) | ((gn & 0xff) << 8) | (bl & 0xff);
}
